import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/siteData';
import { categoryContent, categoryMeta } from '@/mocks/categoryData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useEffect } from 'react';

const allSlugs = categories.map(c => c.slug);

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'japan-korea';
  const meta = categoryMeta[currentSlug] || categoryMeta['japan-korea'];
  const content = categoryContent[currentSlug] || [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentSlug]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: meta.name,
    description: meta.description,
    url: `https://hpmistf.cn/category/${currentSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '亚洲视频精选平台',
      url: 'https://hpmistf.cn',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hpmistf.cn/' },
      { '@type': 'ListItem', position: 2, name: meta.name, item: `https://hpmistf.cn/category/${currentSlug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: meta.name }]} />

          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 pb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2">
              {meta.name}
            </h1>
            <p className="text-sm text-foreground-500 mb-8">{meta.description}</p>

            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 mb-8">
              {allSlugs.map((s) => (
                <Link
                  key={s}
                  to={`/category/${s}`}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    currentSlug === s
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-foreground-600 border border-background-200 hover:border-background-300'
                  }`}
                >
                  {categoryMeta[s]?.name || s}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
              {content.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group flex flex-col sm:flex-row gap-5 bg-white rounded-xl border border-background-200 p-5 hover:border-background-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full sm:w-56 h-36 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs font-medium">
                      {item.duration}
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div>
                      <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                        {item.excerpt}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-foreground-400">
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {(item.views / 10000).toFixed(1)}万</span>
                      <span>{item.date}</span>
                      {item.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded bg-background-100 text-foreground-500">{tag}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}